import React, { useId, useState } from 'react';

import { Input } from '@flixbus/honeycomb-react';

import { CREATE_BREAKDOWN_FLOW_TEXT } from '../texts';
import GroupValidationMessage from './GroupValidationMessage';
import { combineDateTime, splitTimestamp, toDateOnly } from '../utils/incidentTime';
import { isAtOrAfter, isStrictlyBefore } from '../utils/timestampOrder';

import styles from './CushioningInputs.module.css';

const COPY = CREATE_BREAKDOWN_FLOW_TEXT.cushioning;
const ERR = CREATE_BREAKDOWN_FLOW_TEXT.errors;

/**
 * Cushioning timestamps block (no driver). Renders two split date+time pairs
 * — "Cushioning start" and "Cushioning end" — matching the design. The
 * driver is resolved by the parent leaf step (either swapped via cushion
 * choice in busDeadhead, or locked to the original driver elsewhere).
 *
 * Emits `{ fromTimestamp, toTimestamp }` as combined `YYYY-MM-DDTHH:mm` so
 * downstream payload mapping stays shape-stable.
 *
 * @param {{
 *   value: { fromTimestamp: string, toTimestamp: string },
 *   incidentTimestamp?: string,
 *   onChange: (next: { fromTimestamp: string, toTimestamp: string }) => void,
 * }} props
 */
const CushioningInputs = ({ value, incidentTimestamp = '', onChange }) => {
	const startDateId = useId();
	const startTimeId = useId();
	const endDateId = useId();
	const endTimeId = useId();

	const [initialStartDate, initialStartTime] = splitTimestamp(value?.fromTimestamp);
	const [initialEndDate, initialEndTime] = splitTimestamp(value?.toTimestamp);
	const [fromDate, setFromDate] = useState(initialStartDate);
	const [fromTime, setFromTime] = useState(initialStartTime);
	const [toDate, setToDate] = useState(initialEndDate);
	const [toTime, setToTime] = useState(initialEndTime);

	const emitChange = (nextFromDate, nextFromTime, nextToDate, nextToTime) => {
		onChange({
			fromTimestamp: combineDateTime(nextFromDate, nextFromTime),
			toTimestamp: combineDateTime(nextToDate, nextToTime),
		});
	};

	const handleFromDate = (event) => {
		const next = event.target.value;
		setFromDate(next);
		emitChange(next, fromTime, toDate, toTime);
	};
	const handleFromTime = (event) => {
		const next = event.target.value;
		setFromTime(next);
		emitChange(fromDate, next, toDate, toTime);
	};
	const handleToDate = (event) => {
		const next = event.target.value;
		setToDate(next);
		emitChange(fromDate, fromTime, next, toTime);
	};
	const handleToTime = (event) => {
		const next = event.target.value;
		setToTime(next);
		emitChange(fromDate, fromTime, toDate, next);
	};

	const currentFrom = combineDateTime(fromDate, fromTime);
	const currentTo = combineDateTime(toDate, toTime);
	const startBeforeIncident = !isAtOrAfter(currentFrom, incidentTimestamp);
	// Only surface end-before-start once the start-vs-incident conflict is
	// resolved — the user has to fix the earlier constraint first.
	const endBeforeStart = !startBeforeIncident && !isStrictlyBefore(currentFrom, currentTo);
	const startPairError = startBeforeIncident ? ERR.cushionStartBeforeIncident : undefined;
	const endPairError = endBeforeStart ? ERR.cushionEndBeforeStart : undefined;
	const startMin = toDateOnly(incidentTimestamp);
	const endMin = toDateOnly(currentFrom);

	return (
		<div className={styles.row}>
			<div className={styles.pairWithError}>
				<div className={styles.dateTimePair}>
					<div className={styles.dateField}>
						<Input
							id={startDateId}
							type="date"
							label={COPY.startsLabel}
							value={fromDate}
							onChange={handleFromDate}
							min={startMin}
							valid={startBeforeIncident ? false : undefined}
							data-testid="cushioning-from-date-input"
						/>
					</div>
					<div className={styles.timeField}>
						<Input
							id={startTimeId}
							type="time"
							aria-label={COPY.startsTimeAriaLabel}
							value={fromTime}
							onChange={handleFromTime}
							valid={startBeforeIncident ? false : undefined}
							data-testid="cushioning-from-time-input"
						/>
					</div>
				</div>

				<GroupValidationMessage message={startPairError} testId="cushioning-start-error" />
			</div>

			<div className={styles.pairWithError}>
				<div className={styles.dateTimePair}>
					<div className={styles.dateField}>
						<Input
							id={endDateId}
							type="date"
							label={COPY.endsLabel}
							value={toDate}
							onChange={handleToDate}
							min={endMin}
							valid={endBeforeStart ? false : undefined}
							data-testid="cushioning-to-date-input"
						/>
					</div>
					<div className={styles.timeField}>
						<Input
							id={endTimeId}
							type="time"
							aria-label={COPY.endsTimeAriaLabel}
							value={toTime}
							onChange={handleToTime}
							valid={endBeforeStart ? false : undefined}
							data-testid="cushioning-to-time-input"
						/>
					</div>
				</div>

				<GroupValidationMessage message={endPairError} testId="cushioning-end-error" />
			</div>
		</div>
	);
};

export default CushioningInputs;
